import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Select, Upload, message, Popconfirm } from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';

const { Dragger } = Upload;
const { Option } = Select;

interface FileUploadPopupProps {
  visible: boolean;
  onCancel: () => void;
  onContinue: (file: any, year?: number) => void;
  indicatorId: string;
}

const FileUploadPopup: React.FC<FileUploadPopupProps> = ({ visible, onCancel, onContinue, indicatorId }) => {
  const [form] = Form.useForm();
  const [file, setFile] = useState<any>(null);
  const { t } = useTranslation();
  const [hasChanges, setHasChanges] = useState(false); // Track changes

  useEffect(() => {
    if (visible) {
      form.resetFields();
      setFile(null); // Clear previous file
      setHasChanges(false);
    }
  }, [visible, form]);

  const handleFileChange = (info: any) => {
    if (info.file.status === 'done') {
      message.success(`${info.file.name} + t('file.uploaded-successfully')`);
      setFile(info.file);
      setHasChanges(true); // Mark changes
    } else if (info.file.status === 'error') {
      message.error(`${info.file.name} + t('msg.upload.failed')`);
    }
  };

  const beforeUpload = (file: any) => {
    const isExcelOrCSV = file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' || file.type === 'text/csv';
    const isFileSizeValid = file.size > 0 && file.size / 1024 / 1024 < 5; // Ensure size > 0KB and less than 5MB

    if (!isExcelOrCSV) {
      message.error(t('msg.upload.file'));
      return Upload.LIST_IGNORE;
    }

    if (!isFileSizeValid) {
      message.error(t('msg.upload.filesize'));
      return Upload.LIST_IGNORE;
    }

    setFile(file);
    setHasChanges(true); // Mark changes
    return false; // Prevent automatic upload, handle upload manually
  };

  const handleContinue = () => {
    form.validateFields().then(values => {
      if (file) {
        // Pass year to onContinue if indicatorId is 13 or 16
        if (indicatorId === '13' || indicatorId === '16') {
          onContinue(file, values.year);
        } else {
          onContinue(file);
        }
      } else {
        message.error(t('msg.upload.before'));
      }
    });
  };

  const uploadProps = {
    name: 'file',
    multiple: false,
    onChange: handleFileChange,
    beforeUpload,
    fileList: file ? [file] : [],
  };

  const yearOptions = [];
  const currentYear = new Date().getFullYear();
  for (let year = currentYear; year >= 2000; year--) {
    yearOptions.push(
      <Option key={year} value={year}>
        {year}
      </Option>
    );
  }

  const handleCancel = () => {
    if (hasChanges) {
      // Show confirmation if changes exist
      return;
    }
    onCancel(); // Directly cancel if no changes
  };


  return (
    <Modal
      title={t('msg.upload.file.title')}
      open={visible}
      onCancel={onCancel}
      footer={[
        <Popconfirm
          key="cancel"
          title={t('cancel.confirmation.msg')}
          onConfirm={onCancel}
          okText={t('yes')}
          cancelText={t('no')}
          disabled={!hasChanges} // Only enable Popconfirm if there are changes
        >
          <Button key="cancel" onClick={handleCancel}>
            {t('btn.cancel')}
          </Button>
        </Popconfirm>,
        <Button key="continue" type="primary" onClick={handleContinue} disabled={!file}>
          {t('btn.continue')}
        </Button>,
      ]}
    >
      <Form form={form} layout="vertical" onValuesChange={() => setHasChanges(true)} >
        {/* Conditionally render the single year dropdown for indicatorId 13 and 16 */}
        {(indicatorId === '13' || indicatorId === '16') && (
          <Form.Item
            label={t('select-year')}
            name="year"
            rules={[
              {
                required: true,
                message: t("select.year")
              }
            ]}
          >
            <Select placeholder={t('select-year-placeholder')} showSearch={true}> {yearOptions}</Select>
          </Form.Item>
        )}

        <Form.Item
          label={t('upload.file')}
          required
          rules={[{ required: true, message: t('upload.file.msg') }]}
        >
          <Dragger {...uploadProps}>
            <p className="ant-upload-drag-icon">
              <UploadOutlined />
            </p>
            <p className="ant-upload-text">{t('upload.dragfile.msg')}</p>
          </Dragger>
        </Form.Item>
      </Form>
      <p>{t('upload.format.accepted')}</p>
    </Modal>
  );
};

export default FileUploadPopup;
